/**
 * airan-ntn — Main Simulation Loop
 * Round-based execution: mobility → channel → UE selection → FL training → xApp decisions → Space RIC → metrics
 */

import {
  SimConfig, SimState, Satellite, UserEquipment, GroundStation,
  RoundMetrics, ModelWeights, TrainSample, E2KpmReport, E2RcAction,
  SpaceRicState, XAppBase, XAppType, SatelliteState
} from './types.js';
import { DEFAULT_CONFIG } from './types.js';
import { initTopology, ueWalkStep } from './topology.js';
import { refreshChannels, noisePowerdBm, computeSNR, shannonCapacity, commCostBits } from './channel.js';
import { partitionData } from './topology.js';
import { createFLStrategy, selectUEs } from './fl/index.js';
import { createScheduler } from './fl/NtnScheduler.js';
import { createAllXApps } from './xapp/index.js';
import { propagateSatellite, calculateISLLinks, updateFeederLinks, calculateElevation } from './orbital.js';
import { vec3Len, vec3Sub, vec3Norm, vec3Dot } from './types.js';
import { setSeed } from './channel.js';

// ──────────────────────────────────────────────────────────────────────────────
// KPM Report Generation
// ──────────────────────────────────────────────────────────────────────────────

function generateKpmReport(
  sat: Satellite,
  ues: UserEquipment[],
  config: SimConfig,
  round: number
): E2KpmReport {
  const servedUes = ues.filter((u: UserEquipment) => u.servingSatId === sat.id);

  const ueReports = servedUes.map((ue: UserEquipment) => ({
    ueId: ue.id,
    rsrp: -100 + ue.snr,
    rsrq: -10 + ue.snr * 0.5,
    sinr: ue.snr,
    cqi: Math.min(15, Math.max(1, Math.round(ue.snr / 2 + 7))),
    throughput: shannonCapacity(ue.snr) * config.bandwidthMHz * 1e6 / 1e6,
    latency: 10 + Math.max(0, 50 - ue.snr),
    harqAckRate: Math.min(1, 0.5 + ue.snr / 30),
    harqNackRate: Math.max(0, 0.5 - ue.snr / 30),
    mcs: Math.min(28, Math.max(0, Math.round(ue.snr / 2))),
    prbUtilization: Math.min(1, servedUes.length / 100),
    bufferStatus: ue.dataSize * 1000
  }));

  const totalPrbs = 100;
  const usedPrbs = Math.min(totalPrbs, servedUes.length * 5);

  const sliceMetrics = [
    { sliceId: 1, throughput: 50, latency: 20, reliability: 0.99, prbAllocated: 40, prbUsed: 35, ueCount: servedUes.filter((u: UserEquipment) => u.id % 3 === 0).length },
    { sliceId: 2, throughput: 10, latency: 5, reliability: 0.999, prbAllocated: 30, prbUsed: 25, ueCount: servedUes.filter((u: UserEquipment) => u.id % 3 === 1).length },
    { sliceId: 3, throughput: 1, latency: 100, reliability: 0.95, prbAllocated: 30, prbUsed: 20, ueCount: servedUes.filter((u: UserEquipment) => u.id % 3 === 2).length }
  ];

  const elevation = servedUes.length > 0 ? calculateElevation(servedUes[0].position, sat.position) : 0;
  const doppler = servedUes.length > 0 ? servedUes[0].dopplerShift : 0;

  return {
    gnbId: sat.id,
    timestamp: round,
    ueReports,
    cellLoad: usedPrbs / totalPrbs,
    numActiveUes: servedUes.length,
    totalPrbs,
    usedPrbs,
    sliceMetrics,
    elevation,
    dopplerShift: doppler,
    timeToEclipse: sat.state === 'ECLIPSE' ? 0 : 3600,
    batterySoc: sat.batterySoc,
    solarPower: sat.solarPower,
    beamIndex: 0,
    beamGain: 20,
    dcActive: false,
    dcBearers: 0
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// xApp Decision Cycle
// ──────────────────────────────────────────────────────────────────────────────

function runXAppCycle(
  xApps: Map<XAppType, XAppBase>,
  kpmDatabase: Map<number, E2KpmReport[]>,
  satellites: Satellite[],
  ues: UserEquipment[],
  config: SimConfig,
  round: number
): E2RcAction[] {
  const actions: E2RcAction[] = [];

  for (const [, xapp] of xApps) {
    if (!xapp.config.enabled) continue;

    for (const sat of satellites) {
      const reports = kpmDatabase.get(sat.id);
      if (reports && reports.length > 0) {
        const latestReport = reports[reports.length - 1];
        if (latestReport) {
          xapp.processKpmReport(latestReport);
        }
      }
    }

    const decision = xapp.decisionCycle();
    actions.push(...decision.actions);
  }

  return actions;
}

// ──────────────────────────────────────────────────────────────────────────────
// Space RIC Cycle
// ──────────────────────────────────────────────────────────────────────────────

function runSpaceRicCycle(
  spaceRics: Map<number, SpaceRicState>,
  satellites: Satellite[],
  ues: UserEquipment[],
  config: SimConfig,
  round: number
): { actions: E2RcAction[]; gradients: Map<number, ModelWeights> } {
  const actions: E2RcAction[] = [];
  const gradients = new Map<number, ModelWeights>();

  for (const [satId, spRic] of spaceRics) {
    const sat = satellites.find((s: Satellite) => s.id === satId);
    if (!sat) continue;

    const feederActive = sat.feederLink?.active ?? false;
    if (!feederActive) {
      spRic.feederOutageDuration += config.roundDuration;
    } else {
      spRic.feederOutageDuration = 0;
    }

    if (config.autonomousMode && spRic.feederOutageDuration >= config.feederOutageThreshold) {
      spRic.autonomous = true;
      sat.state = 'AUTONOMOUS';
    } else if (feederActive && spRic.autonomous) {
      spRic.autonomous = false;
      sat.state = 'SYNCING';
    }

    if (spRic.autonomous) {
      for (const [dAppName, decision] of spRic.dAppDecisions) {
        actions.push({
          type: 'BEAM_SWITCH',
          targetId: satId,
          payload: { dApp: dAppName, action: decision.action },
          priority: 50,
          timestamp: round
        });
      }
    }

    if (sat.localGradients) {
      gradients.set(satId, sat.localGradients);
    }
  }

  if (config.islCoordination) {
    for (const [satId, spRic] of spaceRics) {
      const sat = satellites.find((s: Satellite) => s.id === satId);
      if (!sat) continue;

      for (const neighborId of sat.islNeighbors) {
        const neighbor = spaceRics.get(neighborId);
        if (neighbor) {
          spRic.islNeighbors.set(neighborId, neighbor);
        }
      }
    }
  }

  return { actions, gradients };
}

// ──────────────────────────────────────────────────────────────────────────────
// Main Simulator Class
// ──────────────────────────────────────────────────────────────────────────────

export class AiranNtnSimulator {
  public state: SimState;
  private flStrategy: ReturnType<typeof createFLStrategy>;
  private scheduler: ReturnType<typeof createScheduler>;
  private xApps: Map<XAppType, XAppBase>;

  constructor(config: SimConfig = DEFAULT_CONFIG) {
    setSeed(config.seed);
    const initialState = initTopology(config);
    this.state = initialState;
    this.flStrategy = createFLStrategy(config);
    this.scheduler = createScheduler(config);
    this.xApps = createAllXApps(config);
  }

  reset(config?: SimConfig): void {
    const newConfig = config ?? this.state.config;
    setSeed(newConfig.seed);
    this.state = initTopology(newConfig);
    this.flStrategy = createFLStrategy(newConfig);
    this.scheduler = createScheduler(newConfig);
    this.xApps = createAllXApps(newConfig);
  }

  getState(): Readonly<SimState> {
    return this.state;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Single FL Round
  // ──────────────────────────────────────────────────────────────────────────

  step(): RoundMetrics {
    const { state } = this;
    const { config, satellites, groundStations, ues, globalWeights } = state;
    state.round++;

    // 1. Satellite propagation (orbital mechanics)
    const dt = config.roundDuration;
    const newSatellites = satellites.map((sat: Satellite) => propagateSatellite(sat, dt));

    // 2. Recalculate ISL links
    const islLinks = calculateISLLinks(newSatellites, config.maxISLRange * 1000);
    for (const sat of newSatellites) {
      sat.islLinks = islLinks.get(sat.id) ?? [];
      sat.islNeighbors = sat.islLinks.map((l: { neighborId: number }) => l.neighborId);
    }

    // 3. Update feeder links
    updateFeederLinks(newSatellites, groundStations, config.minElevationDeg);

    // 4. UE Mobility (Lévy walk)
    ueWalkStep(ues, newSatellites, config);

    // 5. Update UE serving satellite & channel
    refreshChannels(ues, newSatellites, config, dt);

    // 6. Generate KPM reports for each satellite
    for (const sat of newSatellites) {
      const report = generateKpmReport(sat, ues, config, state.round);
      if (!state.kpmDatabase.has(sat.id)) {
        state.kpmDatabase.set(sat.id, []);
      }
      const db = state.kpmDatabase.get(sat.id)!;
      db.push(report);
      if (db.length > 10) db.shift();
    }

    // 7. UE Selection (NTN-aware scheduler)
    const k = Math.min(config.selectedPerRound, ues.length);
    const participants = selectUEs(this.scheduler, ues, k, state.round, newSatellites, config);
    const selectedIds = new Set(participants.map((u: UserEquipment) => u.id));
    ues.forEach((u: UserEquipment) => { u.selected = selectedIds.has(u.id); });
    participants.forEach((u: UserEquipment) => { u.lastRound = state.round; });

    // 8. Local FL Training
    const data = partitionData(config);
    for (const ue of participants) {
      const localData = data.get(ue.id) ?? [];
      this.flStrategy.localTrain(ue, globalWeights, localData, config);
    }

    // 9. Global Aggregation
    state.globalWeights = this.flStrategy.aggregate(globalWeights, participants, data, config);

    // 10. Evaluate on all data
    const allSamples: TrainSample[] = [];
    for (const [, samples] of data) allSamples.push(...samples);
    const globalLoss = this.evaluateLoss(allSamples, state.globalWeights);
    const globalAccuracy = this.evaluateAccuracy(allSamples, state.globalWeights);

    // 11. xApp Decision Cycle
    const xappActions = runXAppCycle(this.xApps, state.kpmDatabase, newSatellites, ues, config, state.round);
    state.pendingActions.push(...xappActions);

    // 12. Space RIC Cycle
    if (config.spaceRicEnabled) {
      const spaceRicResult = runSpaceRicCycle(state.spaceRics, newSatellites, ues, config, state.round);
      state.pendingActions.push(...spaceRicResult.actions);
    }

    // 13. Execute pending actions (simplified)
    this.executeActions(state.pendingActions, newSatellites, ues);
    state.pendingActions = [];

    // 14. Compute metrics
    const snrs = participants.map((u: UserEquipment) => u.snr);
    const avgSNR = snrs.length > 0 ? snrs.reduce((a: number, b: number) => a + b, 0) / snrs.length : 0;
    const minSNR = snrs.length > 0 ? Math.min(...snrs) : 0;

    const elevations = participants
      .map((u: UserEquipment) => u.servingSatId !== null ? calculateElevation(u.position, newSatellites.find((s: Satellite) => s.id === u.servingSatId)!.position) : 0)
      .filter((e: number) => e > 0);
    const avgElevation = elevations.length > 0 ? elevations.reduce((a: number, b: number) => a + b, 0) / elevations.length : 0;

    const dopplers = participants.map((u: UserEquipment) => Math.abs(u.dopplerShift));
    const avgDoppler = dopplers.length > 0 ? dopplers.reduce((a: number, b: number) => a + b, 0) / dopplers.length : 0;

    const feederActive = participants.filter((u: UserEquipment) =>
      u.servingSatId !== null && newSatellites.find((s: Satellite) => s.id === u.servingSatId)?.feederLink?.active
    ).length;
    const feederLinkUptime = participants.length > 0 ? feederActive / participants.length : 0;

    const nParams = globalWeights.w.length + globalWeights.b.length;
    const totalBits = participants.reduce((s: number, u: UserEquipment) => s + commCostBits(nParams, u.snr), 0);

    let handovers = 0;
    for (const ue of ues) {
      if (ue.servingSatId !== ue.bsId && ue.lastRound === state.round) handovers++;
    }

    const satelliteEnergy = newSatellites.reduce((s: number, sat: Satellite) => s + (sat.solarPower - 200) * dt / 3600, 0);

    const metrics: RoundMetrics = {
      round: state.round,
      selectedIds: participants.map((u: UserEquipment) => u.id),
      globalLoss,
      globalAccuracy,
      avgSNR,
      minSNR,
      participationRate: participants.length / ues.length,
      commCostBits: totalBits,
      avgElevation,
      avgDoppler,
      feederLinkUptime,
      handovers,
      satelliteEnergy
    };

    state.history.push(metrics);
    state.satellites = newSatellites;

    return metrics;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Run All Rounds
  // ──────────────────────────────────────────────────────────────────────────

  runAll(onRound?: (m: RoundMetrics) => void): RoundMetrics[] {
    while (this.state.round < this.state.config.totalRounds) {
      const m = this.step();
      if (onRound) onRound(m);
    }
    return this.state.history;
  }

  get isDone(): boolean {
    return this.state.round >= this.state.config.totalRounds;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────────────

  private executeActions(
    actions: E2RcAction[],
    satellites: Satellite[],
    ues: UserEquipment[]
  ): void {
    for (const action of actions) {
      switch (action.type) {
        case 'HANDOVER_TRIGGER': {
          const ueId = action.targetId;
          const targetSatId = (action.payload.targetSatId as number) ?? 0;
          const ue = ues.find((u: UserEquipment) => u.id === ueId);
          if (ue && ue.candidateSats.includes(targetSatId)) {
            ue.servingSatId = targetSatId;
            ue.bsId = targetSatId;
          }
          break;
        }
        case 'BEAM_SWITCH': {
          const satId = action.targetId;
          const sat = satellites.find((s: Satellite) => s.id === satId);
          if (sat) {
            // Beam switch logic
          }
          break;
        }
        case 'TX_POWER_CONTROL': {
          const ueId = action.targetId;
          const ue = ues.find((u: UserEquipment) => u.id === ueId);
          if (ue) {
            ue.txPower = (action.payload.power as number) ?? ue.txPower;
          }
          break;
        }
        case 'SLICE_PRB_ALLOCATION': {
          break;
        }
      }
    }
  }

  private evaluateLoss(samples: TrainSample[], weights: ModelWeights): number {
    if (samples.length === 0) return 0;
    let total = 0;
    for (const s of samples) {
      const probs = this.forward(s.x, weights);
      total -= Math.log(Math.max(probs[s.y], 1e-12));
    }
    return total / samples.length;
  }

  private evaluateAccuracy(samples: TrainSample[], weights: ModelWeights): number {
    if (samples.length === 0) return 0;
    let correct = 0;
    for (const s of samples) {
      const probs = this.forward(s.x, weights);
      const pred = probs.indexOf(Math.max(...probs));
      if (pred === s.y) correct++;
    }
    return correct / samples.length;
  }

  private forward(x: Float32Array, m: ModelWeights): number[] {
    const { w, b, numFeatures, numClasses } = m;
    const logits = new Array(numClasses).fill(0);
    for (let c = 0; c < numClasses; c++) {
      let z = b[c];
      for (let f = 0; f < numFeatures; f++) z += w[c * numFeatures + f] * x[f];
      logits[c] = z;
    }
    const max = Math.max(...logits);
    const exps = logits.map((v: number) => Math.exp(v - max));
    const sum = exps.reduce((a: number, b: number) => a + b, 0);
    return exps.map((v: number) => v / sum);
  }
}